import React, { useEffect, useMemo, useRef, useState } from 'react';

import WebViewer, { Core, WebViewerInstance } from '@pdftron/webviewer';

import { getTodayDate, signatureMethod } from './Helper';
import { useUserData } from '../../App/Component/UserDataProvider';
import { APRYSE_LICENSE_KEY } from '../../Configuration/global';
import { draftStatus } from '../State/DraftState';

const DocxViewerComponent = (props: any) => {
  const {
    fileLink,
    setInstance,
    dragOver,
    drop,
    draftDetails,
    instance,
    isExternal,
    setDocumentLoading,
    internalSignatoryId,
    signatureFormat,
    externalSignatoryId,
    clearSignatureVersion,
  } = props;

  const { user_data, external_email_id, external_signatory_id, isCopyBlock } =
    useUserData();
  const documentContainer = useRef<HTMLDivElement>(null);
  const processedSignatureIdsRef = useRef<Set<string>>(new Set());
  const signedWidgetIdsRef = useRef<Set<string>>(new Set());
  const [signatureAnnot, setSignatureAnnot] = useState<any | null>(null);

  const handleClearSignature = () => {
    if (!instance) return;
    const { Core } = instance;
    const { annotationManager, Annotations } = Core;

    const userAnnotations = annotationManager
      .getAnnotationsList()
      .filter(
        (annot: any) =>
          annot instanceof Annotations.SignatureWidgetAnnotation &&
          annot.getCustomData('user') === id
      );

    annotationManager.deleteAnnotations(userAnnotations, { force: true });

    processedSignatureIdsRef.current.clear();
    signedWidgetIdsRef.current.clear();
    setSignatureAnnot(null);
    const signatureTool = instance.Core.documentViewer.getTool(
      instance.Core.Tools.ToolNames.SIGNATURE
    );
    signatureTool.clearSavedSignatures?.();
    if (signatureTool?.deactivate instanceof Function) {
      signatureTool.deactivate();
    }
    // Clear persisted redux state
    localStorage.removeItem('persist:search-wc-document-viewer');
    localStorage.removeItem('persist:viewer-wc-document-viewer');
  };

  useEffect(() => {
    processedSignatureIdsRef.current.clear();
    signedWidgetIdsRef.current.clear();
    setSignatureAnnot(null);
  }, [clearSignatureVersion]);

  const id = useMemo(
    () =>
      isExternal
        ? externalSignatoryId || external_signatory_id || external_email_id
        : internalSignatoryId || user_data?.email,
    [
      isExternal,
      user_data?.email,
      external_email_id,
      internalSignatoryId,
      external_signatory_id,
      externalSignatoryId,
    ]
  );

  const userIsSignatory = useMemo(() => {
    return draftDetails?.signatories?.find((signatory: any) => {
      const matchId =
        signatory?.signatory_id === internalSignatoryId ||
        signatory?.signatory_id === external_signatory_id ||
        signatory?.signatory_id === externalSignatoryId;

      const matchEmail = isExternal
        ? signatory?.email === external_email_id
        : signatory?.email === user_data?.email;

      return matchId || matchEmail;
    });
  }, [
    draftDetails,
    internalSignatoryId,
    external_signatory_id,
    user_data?.email,
    externalSignatoryId,
  ]);

  const disabledSignatureMethods = useMemo(() => {
    return signatureMethod.filter(
      (method) => !signatureFormat?.includes(method)
    );
  }, [signatureFormat]);

  const signatoryColor = useMemo(() => {
    if (!draftDetails?.signatories?.length) return '#00008b';
    const matchedSignatory = draftDetails?.signatories?.find(
      (signatory: any) =>
        signatory?.signatory_id === internalSignatoryId ||
        signatory?.signatory_id === external_signatory_id ||
        signatory?.signatory_id === externalSignatoryId ||
        signatory?.email === user_data?.email ||
        signatory?.email === external_email_id
    );
    if (matchedSignatory?.color) {
      const { r, g, b } = matchedSignatory?.color;
      return `rgb(${r}, ${g}, ${b})`;
    }
    return '#00008b';
  }, [
    draftDetails,
    user_data?.email,
    external_email_id,
    internalSignatoryId,
    external_signatory_id,
    externalSignatoryId,
  ]);

  useEffect(() => {
    processedSignatureIdsRef.current.clear();
    signedWidgetIdsRef.current.clear();
    setSignatureAnnot(null);
    localStorage.removeItem('persist:search-wc-document-viewer');
    localStorage.removeItem('persist:viewer-wc-document-viewer');
    if (documentContainer.current) {
      documentContainer.current.innerHTML = '';
    }

    if (fileLink && documentContainer.current) {
      setDocumentLoading?.(true);

      WebViewer(
        {
          path: '/webviewer',
          fullAPI: true,
          css: '/styles/webviewer.css',
          ui: 'legacy',
          disabledElements: [
            'header',
            'toolsHeader',
            'linkButton',
            'annotationCommentButton',
            'contextMenuPopup',
          ],
          licenseKey: APRYSE_LICENSE_KEY,
        },
        documentContainer.current
      ).then((instance: WebViewerInstance) => {
        const { Core, UI } = instance;
        setInstance(instance);
        if (isCopyBlock) {
          UI.disableFeatures([UI.Feature.Copy]);
        }
        const { documentViewer, Annotations } = Core;
        documentViewer.loadDocument(fileLink);

        UI.annotationPopup.update([{ type: 'ac' }]);

        documentViewer.addEventListener('documentLoaded', () => {
          handleClearSignature();
          UI.setFitMode(UI.FitMode.FitWidth);
          //PDF scrolling fix
          const scrollElement =
            documentViewer.getScrollViewElement() as HTMLElement | null;
          if (scrollElement) {
            scrollElement.style.overflowY = 'auto';
            scrollElement.style.overflowX = 'auto';
            scrollElement.style.setProperty(
              '-webkit-overflow-scrolling',
              'touch'
            );
            scrollElement.style.touchAction = 'pan-y';
            scrollElement.style.overscrollBehaviorY = 'contain';
          }
          if (documentContainer.current) {
            documentContainer.current.style.touchAction = 'pan-y';
            documentContainer.current.style.overflow = 'hidden';
          }
          setDocumentLoading?.(false);
          UI.enableFeatures([instance.UI.Feature.Initials]);

          // Optional: Clear annotations on load
          Core.annotationManager.deleteAnnotations(
            Core.annotationManager.getAnnotationsList()
          );
        });

        const doc = documentContainer.current;
        if (doc) {
          doc.addEventListener('dragover', dragOver);
          doc.addEventListener('drop', (e: any) => drop(e, documentViewer));
        }

        Annotations.setCustomCreateSignHereElementHandler(function (
          tool,
          { annotation, originalCreateSignHereElement }
        ) {
          const signHereElement = originalCreateSignHereElement(tool);
          if (annotation.getCustomData('type') === 'initials') {
            signHereElement.innerHTML = 'Initials';
          } else {
            signHereElement.innerHTML = 'Sign Here';
          }
          signHereElement.style.backgroundColor = signatoryColor;
          return signHereElement;
        });
      });
    }
  }, [fileLink]);

  useEffect(() => {
    if (!instance || !signatureFormat) return;
    const { UI } = instance;
    const collab = !draftDetails?.open_collab ? ['textPopup'] : [];
    if (disabledSignatureMethods?.length) {
      if ((signatureFormat ?? []).includes('textSignaturePanelButton')) {
        UI.setSelectedTab('signatureModal', 'textSignaturePanelButton');
      } else {
        UI.setSelectedTab(
          'signatureModal',
          signatureFormat?.[0] ?? 'textSignaturePanelButton'
        );
      }
      UI.disableElements([
        ...disabledSignatureMethods,
        ...collab,
        'toolsOverlay',
      ]);
    } else {
      UI.setSelectedTab('signatureModal', 'textSignaturePanelButton');
      UI.disableElements([...collab, 'toolsOverlay', 'textPopup']);
    }
  }, [
    instance,
    signatureFormat,
    disabledSignatureMethods,
    draftDetails?.open_collab,
  ]);

  useEffect(() => {
    if (!instance) return;
    const { Core } = instance;
    if (
      userIsSignatory &&
      !userIsSignatory?.signed_date &&
      draftDetails?.signature_method === 'Stylus (Riverus)'
    ) {
      const capitalize = (str: string) =>
        str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

      const name =
        userIsSignatory?.user_type === 'external'
          ? capitalize(userIsSignatory?.name)
          : `${capitalize(userIsSignatory?.first_name || userIsSignatory?.name)} ${capitalize(userIsSignatory?.last_name)}`;

      Core.annotationManager.setCurrentUser(name);
    }
  }, [draftDetails, userIsSignatory, instance]);

  useEffect(() => {
    if (!instance) return;
    const { Core } = instance;
    const { Annotations, annotationManager } = Core;

    annotationManager.addEventListener(
      'annotationChanged',
      (
        annotations: Core.Annotations.Annotation[],
        action: string,
        context: { imported: boolean }
      ) => {
        const { imported } = context;
        annotations.forEach((annot: any) => {
          if (annot.Subject?.toLowerCase() === 'signature') {
            if (!processedSignatureIdsRef.current.has(annot.Id)) {
              processedSignatureIdsRef.current.add(annot.Id);
              annot.NoMove = true;
              annot.NoResize = true;
              annot.NoRotate = true;
              annot.setCustomData('userSigned', id);
              annot.setCustomData('user', '');
              if (!annot.getCustomData('signedDate')) {
                annot.setCustomData('signedDate', getTodayDate());
                setSignatureAnnot(annot);
              }
            }
          }

          if (
            draftDetails?.status === draftStatus.SIGNATURE_PENDING ||
            draftDetails?.status === draftStatus?.INTERNAL_SIGNATURE_PENDING ||
            draftDetails?.status === draftStatus?.EXTERNAL_SIGNATURE_PENDING
          ) {
            annot.NoMove = true;
            annot.NoResize = true;
            annot.NoRotate = true;
            annot.ReadOnly = true;
            Core.Tools.Tool.ENABLE_TEXT_HIGHLIGHT_CREATE = false;
            if (annot.Subject?.toLowerCase() === 'underline') {
              annot.Hidden = true;
              annot.Listable = false;
              annotationManager.deleteAnnotation(annot);
            }
          }
        });

        if (imported && action === 'add') {
          annotations.forEach((annot: any) => {
            if (
              annot instanceof Annotations.WidgetAnnotation &&
              annot.getCustomData('user') &&
              annot.getCustomData('user') !== id
            ) {
              annot.Hidden = true;
              annot.Listable = false;
            }
          });
        }

        if (draftDetails?.status === draftStatus.SIGNATURE_ABORTED) {
          annotations.forEach((annot: any) => {
            annotationManager.deleteAnnotation(annot);
          });
        }
      }
    );

    if (!draftDetails?.open_collab) {
      // instance.UI.disableElements(['textPopup']);
    }
  }, [draftDetails, instance, id]);

  useEffect(() => {
    if (!instance) return;
    const { Core } = instance;
    const { annotationManager } = Core;
    annotationManager.addEventListener(
      'annotationChanged',
      async (annotations: Core.Annotations.Annotation[], action: string) => {
        const annotToDraw: any = [];

        if (action === 'modify') {
          let selectedAnnot: any = [];
          annotations.map((annot: any) => {
            selectedAnnot = annotationManager
              .getAnnotationsList()
              .filter(
                (allAnnot: any) =>
                  allAnnot.getCustomData('value') ===
                    annot.getCustomData('value') &&
                  allAnnot.getCustomData('id') === annot.getCustomData('id')
              );
            selectedAnnot.map((selected: any) => {
              selected.setX(annot.getX());
              selected.setY(annot.getY());
              selected.Width = annot.getWidth();
              selected.Height = annot.getHeight();
              annotToDraw.push(selected);
            });
          });
          if (annotToDraw.length) {
            await annotationManager.drawAnnotationsFromList(annotToDraw);
          }
        }
      }
    );
  }, [instance]);

  useEffect(() => {
    if (!instance || !signatureAnnot) return;
    const { Core } = instance;
    const { Annotations, documentViewer } = Core;
    const annotationManager = documentViewer.getAnnotationManager();
    const widgetAnnots = annotationManager
      .getAnnotationsList()
      .filter(
        (annot: any) =>
          annot instanceof Annotations.SignatureWidgetAnnotation &&
          annot.getCustomData('user') === id &&
          (!annot.getCustomData('trn-signature-type') ||
            annot.getCustomData('trn-signature-type') ===
              signatureAnnot.getCustomData('trn-signature-type')) &&
          !annot.isSignedByAppearance() &&
          annot.rotation === signatureAnnot?.Rotation
      );

    widgetAnnots.forEach((annot: any) => {
      if (signedWidgetIdsRef.current.has(annot.Id)) return;
      const annotCopy = annotationManager.getAnnotationCopy(signatureAnnot);
      annotCopy.PageNumber = annot.getPageNumber();
      annotCopy.X = annot.getX();
      annotCopy.Y = annot.getY();
      annotationManager.addAnnotation(annotCopy);
      annot.sign(annotCopy);
      signedWidgetIdsRef.current.add(annot.Id);
    });

    annotationManager.getAnnotationsList().forEach((annot: any) => {
      if (
        annot instanceof Annotations.WidgetAnnotation &&
        annot.getCustomData('user') === id &&
        annot.getCustomData('trn-signature-type') ===
          signatureAnnot.getCustomData('trn-signature-type')
      ) {
        annot.Hidden = true;
        annot.Listable = false;
      }
    });

    if (widgetAnnots.length > 0) {
      setSignatureAnnot(null);
    }
  }, [instance, signatureAnnot, id]);

  return (
    <div
      ref={documentContainer}
      id="document-viewer"
      style={{
        height: '80vh',
        minHeight: 0,
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        touchAction: 'pan-y',
      }}
    />
  );
};

export default DocxViewerComponent;
